
import Vue from 'vue';
import Component from 'vue-class-component';
import IconChip from '@/core/components/IconChip.vue';
import PaymentChip from '@/core/components/PaymentChip.vue';
import InputSlider from '@/core/components/InputSlider.vue';
import DollarInput from '@/core/components/DollarInput.vue';
import { namespace } from 'vuex-class';
import { LoanDetails } from '@/loan/types/loan-details';
import { LOAN_TAKE_BACK_MINIMUM_AMOUNT } from '@/loan/constants/loan-take-back-minimum-amount';
import { Loan } from '@/loan/types/loan';
import withdrawIconSVG from '@/assets/images/withdrawIcon.svg';
import LoanProjectionChart from '@/loan/components/LoanProjectionChart.vue';
import { LoanProjection } from '@/loan/types/loan-projection';
import { Debouncer } from '@/core/types/debouncer';
import { LoanProjectionRequestQuery } from '@/loan/types/loan-projection-request-query';
import { LoanProjectionType } from '@/loan/enums/loan-projection-type';
import { LoanProjectionChartContext } from '@/loan/types/loan-projection-chart-context';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');
const ICON_CHIP_ACTIVE_TEXT = 'Withdraw your extra contributions if you need them.';
const ICON_CHIP_ACTIVE_TITLE = 'Take-Back your money.';
const ICON_CHIP_DISABLED_TITLE = 'Your Take-Back funds have been temporarily locked.';
const ICON_CHIP_DISABLED_TEXT = (fiName: string, fiPhone: string) => `Please contact ${fiName} at ${UtilHelper.formatPhoneNumber(fiPhone)} for more information.`;
const TRANSACTION_AMOUNT_LESS_THAN_MINIMUM_TEXT = (amount: number) => `Your withdrawal must be at least ${UtilHelper.formatDollar(amount)}`;
const TRANSACTION_AMOUNT_MORE_THAN_MAXIMUM_TEXT = 'Your withdrawal cannot be above your available Take-Back amount or $55,000.';
const TEST_ID = 'Kloans-Loan-LoanTakeBack';

@Component({
	components: {
		IconChip,
		PaymentChip,
		InputSlider,
		DollarInput,
		LoanProjectionChart,
	},
})
export default class LoanTakeBack extends Vue {
	public TEST_ID = TEST_ID;

	@loanNamespace.Getter('amountAvailableToTakeBack')
	public amountAvailableToTakeBack!: number;

	public formatDollar = UtilHelper.formatDollar;

	@loanNamespace.Action('getAndSetProjection')
	public getAndSetProjection!: (
		{ loanId, query }:
		{ loanId: string; query: LoanProjectionRequestQuery }
	) => Promise<void>;

	public iconChipIcon = withdrawIconSVG;

	public isProjectionLoading = false;

	@loanNamespace.Getter('isTakeBackEnabled')
	public isTakeBackEnabled!: boolean;

	@loanNamespace.State('loan')
	public loan!: Loan;

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	public nextPaymentInputDebouncer!: Debouncer<number>;

	public nextPaymentInputErrorMesasge: string | null = null;

	public nextPaymentInputMin = 0;

	public nextPaymentInputValue = 0;

	public maxDwollaPaymentAmount = 55000;

	public paymentChipText = 'Available Take-Back';

	@loanNamespace.State('projection')
	public projection!: LoanProjection;

	public projectionChartContext: LoanProjectionChartContext | null = null;

	@loanNamespace.Getter('startBalance')
	public startBalance!: number;

	get iconChipText(): string {
		if (!this.isTakeBackEnabled) {
			return ICON_CHIP_DISABLED_TEXT(
				this.loan.fiConfig.displayName,
				this.loan.fi.contactPhone,
			);
		}

		if (this.isTakeBackActive) {
			return ICON_CHIP_ACTIVE_TEXT;
		}

		return ICON_CHIP_DISABLED_TEXT(
			this.loan.fiConfig.displayName,
			this.loan.fi.contactPhone,
		);
	}

	get iconChipTitle(): string {
		if (!this.isTakeBackEnabled) {
			return ICON_CHIP_DISABLED_TITLE;
		}

		if (this.isTakeBackActive) {
			return ICON_CHIP_ACTIVE_TITLE;
		}

		return ICON_CHIP_DISABLED_TITLE;
	}

	get isNextPaymentButtonDisabled(): boolean {
		return (this.nextPaymentInputValue < LOAN_TAKE_BACK_MINIMUM_AMOUNT)
			|| (this.nextPaymentInputValue > this.amountAvailableToTakeBack)
			|| (this.nextPaymentInputValue > this.maxDwollaPaymentAmount);
	}

	get isTakeBackActive(): boolean {
		return this.amountAvailableToTakeBack >= LOAN_TAKE_BACK_MINIMUM_AMOUNT;
	}

	get nextPaymentInputMax(): number {
		if (Number(this.amountAvailableToTakeBack) <= this.maxDwollaPaymentAmount) {
			return this.amountAvailableToTakeBack;
		}

		return Math.floor(this.maxDwollaPaymentAmount);
	}

	mounted() {
		this.projectionChartContext = {
			isOriginal: true,
			projection: this.projection,
		};

		this.nextPaymentInputDebouncer = new Debouncer({
			callback: this.setProjectionContextFromInput,
			debounceTime: 300,
			skipIfSame: true,
		});
	}

	public async setProjectionContextFromInput(value: number): Promise<void> {
		this.isProjectionLoading = true;

		const query: LoanProjectionRequestQuery = {
			property: LoanProjectionType.TAKE_BACK,
			value: String(value),
		};

		await this.getAndSetProjection({
			loanId: this.loanId,
			query,
		});

		this.projectionChartContext = {
			isOriginal: false,
			projection: this.projection,
		};

		this.isProjectionLoading = false;
	}

	public onNextPaymentAmountInputChange(value: number): any {
		if (this.nextPaymentInputValue === value) {
			return;
		}

		this.nextPaymentInputValue = value;
		this.nextPaymentInputErrorMesasge = null;

		if (this.nextPaymentInputValue < LOAN_TAKE_BACK_MINIMUM_AMOUNT) {
			this.nextPaymentInputErrorMesasge = TRANSACTION_AMOUNT_LESS_THAN_MINIMUM_TEXT(LOAN_TAKE_BACK_MINIMUM_AMOUNT);
			return;
		}

		if (this.nextPaymentInputValue > this.amountAvailableToTakeBack) {
			this.nextPaymentInputErrorMesasge = TRANSACTION_AMOUNT_MORE_THAN_MAXIMUM_TEXT;
		}

		this.nextPaymentInputDebouncer.debounce(value);
	}
}

